import { HttpRequest } from '../../components/HttpRequest';
import secureLocalStorage from 'react-secure-storage';
import { apiClient } from './config';

export const productsApi = {
  async getGroupedProducts() {
    try {
      const businessNumber = secureLocalStorage.getItem('businessId');
      const response = await HttpRequest({
        method: 'GET',
        url: `/api/v1/ecommerce/grouped_products?businessNumber=${businessNumber}`
      });

      if (response.status === 200 && response.response?.data) {
        // Transform the grouped data into sections for the homepage
        const groupedData = response.response.data.reduce((acc, category) => {
          const products = category?.product?.map((product) => ({
            ...product,
            // Use first pricing variation as default
            defaultPrice: product.pricing?.[0]?.discountedPrice || product.pricing?.[0]?.normalPrice,
            defaultNormalPrice: product.pricing?.[0]?.normalPrice,
            hasVariations: product.pricing?.length > 1
          }));

          const subcategoryProducts = category?.subcategory && category?.subcategory?.length > 0
            ? category?.subcategory?.flatMap((subcategory) =>
                subcategory?.product.map((product) => ({
                  ...product,
                  subcategoryName: subcategory.subcategoryName,
                  parentCategoryName: category.categoryName,
                  // Use first pricing variation as default
                  defaultPrice: product.pricing?.[0]?.discountedPrice || product.pricing?.[0]?.normalPrice,
                  defaultNormalPrice: product.pricing?.[0]?.normalPrice,
                  hasVariations: product.pricing?.length > 1
                }))
              )
            : [];

          const categoryProducts = [...(products || []), ...(subcategoryProducts || [])];

          if (categoryProducts.length > 0) {
            acc.push({
              categoryName: category?.categoryName,
              categoryId: category?._id,
              products: categoryProducts
            });
          }

          return acc;
        }, []);

        return groupedData;
      }
      return [];
    } catch (error) {
      console.error('Error fetching grouped products:', error);
      return [];
    }
  },

  async getAll(page = 1, pageSize = 12) {
    try {
      const businessNumber = secureLocalStorage.getItem('businessId');
      const response = await HttpRequest({
        method: 'GET',
        url: `/api/v1/ecommerce/products?businessNumber=${businessNumber}&page=${page}&pageSize=${pageSize}`
      });

      if (!response) {
        console.error('No response from API');
        return {
          items: [],
          total: 0,
          page: 1,
          pageSize: 12,
          hasMore: false
        };
      }

      if (response.status === 200 && response.response) {
        return {
          items: response.response?.data || [],
          total: response.response?.total || 0,
          page: page,
          pageSize: pageSize,
          hasMore: response.response?.hasMore || false
        };
      }
      return {
        items: [],
        total: 0,
        page: 1,
        pageSize: 12,
        hasMore: false
      };
    } catch (error) {
      console.error('Error fetching products:', error);
      return {
        items: [],
        total: 0,
        page: 1,
        pageSize: 12,
        hasMore: false
      };
    }
  },

  async getById(id) {
    try {
      const businessNumber = secureLocalStorage.getItem('businessId');
      const response = await HttpRequest({
        method: 'GET',
        url: `/api/v1/ecommerce/get_product/${id}?businessNumber=${businessNumber}`
      });

      // The actual product data is nested in response.response.data
      if (response.status === 200 && response.response?.data) {
        return {
          data: response.response.data,
          status: response.status,
          message: response.response.message
        };
      }

      return {
        data: null,
        status: response.status || 404,
        error: 'Product not found'
      };
    } catch (error) {
      console.error('Error fetching product:', error);
      return {
        data: null,
        error: error.message,
        status: 500
      };
    }
  },

  async getByCategory(category) {
    try {
      const businessNumber = secureLocalStorage.getItem('businessId');
      const response = await HttpRequest({
        method: 'GET',
        url: `/api/v1/ecommerce/products?businessNumber=${businessNumber}&category=${category}`
      });

      return response.response?.data || [];
    } catch (error) {
      console.error('Error fetching products by category:', error);
      return [];
    }
  },

  async getFeatured() {
    try {
      const response = await apiClient.get('/products/featured');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching featured products:', error);
      return [];
    }
  },

  async search(query) {
    try {
      const response = await apiClient.get('/products/search', {
        params: { q: query }
      });
      return response.data || [];
    } catch (error) {
      console.error('Error searching products:', error);
      return [];
    }
  },

  async searchProducts(query) {
    // Alias for search method used in SearchModal
    return this.search(query);
  },

  // Get filtered products with advanced filters
  async getFilteredProducts({
    page = 1,
    limit = 10,
    search = '',
    categoryName = '',
    minPrice = 0,
    maxPrice = 0,
    tags = [],
    variations = {},
    sortBy = '',
    team = ''
  }) {
    try {
      const businessNumber = secureLocalStorage.getItem('businessId');

      // Build variation string from variations object
      const variationString = Object.values(variations).flat().join(',');

      const params = new URLSearchParams({
        businessNumber,
        page,
        limit
      });

      // Add optional parameters
      if (search) params.append('search', search);
      if (categoryName) params.append('categoryName', categoryName);
      if (minPrice > 0) params.append('price', minPrice);
      if (maxPrice > 0) params.append('upperPrice', maxPrice);
      if (tags.length > 0) params.append('tags', tags.join(','));
      if (variationString) params.append('variationValue', variationString);
      if (sortBy) params.append('sortBy', sortBy);
      if (team) params.append('team', team);
      

      const response = await HttpRequest({
        method: 'GET',
        url: `/api/v1/ecommerce/get_products?${params.toString()}`
      });

      if (response.status === 200 || response.status === 201) {
        const transformedData = await Promise.all(response.response.data.map(async (item) => {
          const normalPrice = item?.pricing && item.pricing.length > 0 ? item.pricing[0].normalPrice : item?.productPrice;
          const discountedPrice = item?.pricing && item.pricing.length > 0 ? item.pricing[0].discountedPrice : item?.productPrice;

          return {
            id: item?._id,
            productName: item?.productName,
            image: item.featuredImage || item.thumbailImagePath || '',  // Note: API has typo 'thumbailImagePath' instead of 'thumbnailImagePath'
            normalPrice: normalPrice,
            discountedPrice: discountedPrice,
            pricing: item?.pricing,
            isPreOrder: this.checkPreorder(item?.pricing),
            // API may return category/subcategory populated as objects — coerce to names so React can render them
            category: typeof item?.category === 'string' ? item.category : (item?.category?.categoryName || item?.productCategory || ''),
            subcategory: typeof item?.subcategory === 'string' ? item.subcategory : (item?.subcategory?.categoryName || ''),
            tags: item?.tags || []
          };
        }));

        return {
          data: transformedData,
          count: response.response?.count || 0,
          page: page,
          limit: limit,
          hasMore: transformedData.length === limit
        };
      }

      return {
        data: [],
        count: 0,
        page: 1,
        limit: limit,
        hasMore: false
      };
    } catch (error) {
      console.error('Error fetching filtered products:', error);
      return {
        data: [],
        count: 0,
        page: 1,
        limit: limit,
        hasMore: false
      };
    }
  },

  // Get maximum and minimum prices for price filter
  async getMaxMinPrices() {
    try {
      const businessNumber = secureLocalStorage.getItem('businessId');
      const response = await HttpRequest({
        method: 'GET',
        url: `/api/v1/ecommerce/get-max-minpricesinbs?businessNumber=${businessNumber}`
      });

      if (response.status === 200 && response.response) {
        return {
          minPrice: response.response[0]?.minPrice || 0,
          maxPrice: response.response[0]?.maxPrice || 10000
        };
      }

      return {
        minPrice: 0,
        maxPrice: 10000
      };
    } catch (error) {
      console.error('Error fetching price range:', error);
      return {
        minPrice: 0,
        maxPrice: 10000
      };
    }
  },

  // Get business variations (sizes, colors, etc.)
  async getBusinessVariations() {
    try {
      const businessNumber = secureLocalStorage.getItem('businessId');
      const response = await HttpRequest({
        method: 'GET',
        url: `/api/v1/ecommerce/get_business_variations?businessNumber=${businessNumber}`
      });

      if (response.status === 200 && response.response?.data) {
        return response.response.data;
      }

      return [];
    } catch (error) {
      console.error('Error fetching business variations:', error);
      return [];
    }
  },

  // Get business tags
  async getBusinessTags() {
    try {
      const businessNumber = secureLocalStorage.getItem('businessId');
      const response = await HttpRequest({
        method: 'GET',
        url: `/api/v1/ecommerce/business/getTag?businessNumber=${businessNumber}`
      });

      if (response.status === 200 && response.response?.data) {
        return response.response.data;
      }

      return [];
    } catch (error) {
      console.error('Error fetching business tags:', error);
      return [];
    }
  },

  // Helper method to check pre-order status
  checkPreorder(pricingObject) {
    if (!pricingObject || !Array.isArray(pricingObject)) return false;

    const now = new Date();
    return pricingObject.some(item =>
      item.isPreOrderEnabled &&
      new Date(item.preOrderdateEnd) > now &&
      item.preOrderStockLeft > 0
    );
  },

  // Save cart item to backend
  async saveCartItem(cartItem) {
    try {
      const businessNumber = secureLocalStorage.getItem('businessId');
      const response = await HttpRequest({
        method: 'POST',
        url: `/api/v1/ecommerce/savecartitem?businessNumber=${businessNumber}`,
        body: {
          cartItem: cartItem
        }
      });

      return {
        status: response.status || 200,
        data: response.response?.data || null,
        message: response.response?.message || 'Cart item saved'
      };
    } catch (error) {
      console.error('Error saving cart item:', error);
      return {
        status: 500,
        error: error.message
      };
    }
  }
};
